import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../api/client';
import { DbSearchParameters } from '../../models/DbSearchParameters';
import { DbSearchResult } from '../../models/DbSearchResult';
import '../home/home.css';
import DbSearchResultsComponent from './DbSearchResultsComponent';

interface DbSearchFormComponentProps {
   value: DbSearchParameters;
   onChange?: (newValue: DbSearchParameters) => void;
   /** Run a search on mount when the form was opened from a shareable URL. */
   autoSearch?: boolean;
}

const DbSearchFormComponent = (props: DbSearchFormComponentProps): React.JSX.Element => {
   const [value, setValue] = useState<DbSearchParameters>(props.value);
   const [searching, setSearching] = useState<string>('');
   const [showResults, setShowResults] = useState<boolean>(false);
   const [searchResult, setSearchResult] = useState<DbSearchResult | null>(null);

   const runSearch = (query: DbSearchParameters['query']): void => {
      setSearching('Searching ...');

      void (async (): Promise<void> => {
         try {
            const response = await apiFetch<DbSearchResult>('/api/db/search-results', {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify(query),
               cache: 'no-cache',
            });

            setSearchResult(response);
            setShowResults(true);
         } catch (error) {
            console.error('Error during search:', error);
         } finally {
            setSearching('');
         }
      })();
   };

   useEffect(() => {
      if (!props.autoSearch) return;
      const query = props.value.query;
      if (!query.pdb_ids && !query.uniprot_ids && !query.ligands) return;
      runSearch(query);
   }, []);

   const submitForm = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      runSearch(value.query);
   };

   const loadExample1 = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
      event.preventDefault();

      value.query.pdb_ids = '1a0b';
      value.query.uniprot_ids = '';
      value.query.ligands = '';
      value.query.res_threshold = '';
      value.query.xray_only = false;
      value.query.exclude_nmr = false;

      setValue({ ...value });
   };

   const loadExample2 = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
      event.preventDefault();

      value.query.pdb_ids = '1d6z';
      value.query.uniprot_ids = '';
      value.query.ligands = 'CA CU';
      value.query.res_threshold = '';
      value.query.xray_only = false;
      value.query.exclude_nmr = true;

      setValue({ ...value });
   };

   const loadExample3 = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
      event.preventDefault();

      value.query.pdb_ids = '';
      value.query.uniprot_ids = '';
      value.query.ligands = 'HEM';
      value.query.res_threshold = '1.5';
      value.query.xray_only = true;
      value.query.exclude_nmr = false;

      setValue({ ...value });
   };

   /* eslint-disable @typescript-eslint/no-unsafe-assignment */
   /* eslint-disable @typescript-eslint/no-unsafe-member-access */
   /* eslint-disable @typescript-eslint/no-unsafe-call */
   return (
      <div className="submit-form-box">
         <div className="form-header">
            <h1>Search AHoJ-DB</h1>
         </div>

         <form onSubmit={submitForm}>
            <div className="form-group">
               <span style={{ padding: '5px', color: '#666' }}>Specify one or more of the following:</span>
               <div className="examples" style={{ marginTop: 0 }}>
                  <span>
                     <a href="#" onClick={loadExample1}>
                        example 1
                     </a>
                  </span>
                  <span>
                     <a href="#" onClick={loadExample2}>
                        example 2
                     </a>
                  </span>
                  <span>
                     <a href="#" onClick={loadExample3}>
                        example 3
                     </a>
                  </span>
               </div>

               <div className="structure-ligand">
                  <div className="form-group xpdb">
                     <label
                        htmlFor="pdb_ids"
                        className="thick"
                        title="Target chain PDB_ID(s). Multiple values should be delimited by space."
                     >
                        PDB_ID/s:
                     </label>
                     <input
                        type="text"
                        className="form-control"
                        id="pdb_ids"
                        name="pdb_ids"
                        placeholder=""
                        onChange={(e): void =>
                           setValue({
                              ...value,
                              query: {
                                 ...value.query,
                                 pdb_ids: e.target.value,
                              },
                           })
                        }
                        value={value.query.pdb_ids}
                     />
                  </div>
                  <div className="form-group xuniprot">
                     <label
                        htmlFor="uniprot_ids"
                        className="thick"
                        title="Target chain UniProt_ID(s). Multiple values should be delimited by space."
                     >
                        UniProt_ID/s:
                     </label>
                     <input
                        type="text"
                        className="form-control"
                        id="uniprot_ids"
                        name="uniprot_ids"
                        placeholder=""
                        onChange={(e): void =>
                           setValue({
                              ...value,
                              query: {
                                 ...value.query,
                                 uniprot_ids: e.target.value,
                              },
                           })
                        }
                        value={value.query.uniprot_ids}
                     />
                  </div>
                  <div className="form-group xligand">
                     <label
                        htmlFor="ligands"
                        className="thick"
                        title="Target chain Ligand PDB name/s. Multiple values should be delimited by space."
                     >
                        Ligand/s:
                     </label>
                     <input
                        type="text"
                        className="form-control"
                        id="ligands"
                        name="ligands"
                        placeholder=""
                        onChange={(e): void =>
                           setValue({
                              ...value,
                              query: {
                                 ...value.query,
                                 ligands: e.target.value,
                              },
                           })
                        }
                        value={value.query.ligands}
                     />
                  </div>
               </div>

               <div className="options-outer" style={{ marginTop: '1em' }}>
                  <h4 style={{ marginBottom: '0.7em' }}>Filters</h4>
                  <div className="options">
                     <div className="options-left">
                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              name="xray_only"
                              id="xray_only"
                              checked={value.query.xray_only}
                              onChange={(e): void =>
                                 setValue({
                                    ...value,
                                    query: {
                                       ...value.query,
                                       xray_only: e.target.checked,
                                    },
                                 })
                              }
                           />
                           <label
                              className="form-check-label"
                              htmlFor="xray_only"
                              title="Only X-ray structures will be considered during search."
                           >
                              X-ray structures only
                           </label>
                        </div>
                        <div className="form-check" style={{ marginBottom: 0 }}>
                           <input
                              type="checkbox"
                              className="form-check-input"
                              id="exclude_nmr"
                              name="exclude_nmr"
                              checked={value.query.exclude_nmr}
                              onChange={(e): void =>
                                 setValue({
                                    ...value,
                                    query: {
                                       ...value.query,
                                       exclude_nmr: e.target.checked,
                                    },
                                 })
                              }
                           />
                           <label
                              className="form-check-label"
                              htmlFor="exclude_nmr"
                              title="Exclude NMR structures in search results."
                           >
                              Exclude NMR structures
                           </label>
                        </div>
                        <div className="options-left">
                           <div className="form-group option">
                              <label
                                 htmlFor="res_threshold"
                                 title="Target structure minimal resolution. Leave empty for all."
                              >
                                 Resolution&nbsp;threshold:
                              </label>
                              <input
                                 type="text"
                                 className="form-control"
                                 id="res_threshold"
                                 name="res_threshold"
                                 defaultValue={value.query.res_threshold}
                                 onChange={(e): void =>
                                    setValue({
                                       ...value,
                                       query: {
                                          ...value.query,
                                          res_threshold: e.target.value,
                                       },
                                    })
                                 }
                                 style={{ width: '7em' }}
                              />
                              &nbsp;&nbsp;Å
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <br />
            <button type="submit" className="btn btn-primary">
               Search
            </button>
            <div>{searching}</div>
         </form>
         {showResults && searchResult ? <DbSearchResultsComponent searchResult={searchResult} /> : null}
      </div>
   );
};

export default DbSearchFormComponent;
